import { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Upload,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  Check,
  Clipboard,
  FileText,
} from "lucide-react";
import { CustomFields } from "./CustomFields";

export function RenewalWizard({ document, isOpen, onClose, onSubmit }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    issue_date: new Date().toISOString().split("T")[0],
    expiration_date: "",
    cost_amount: "",
    cost_payment_method: "transfer",
    notes: "",
  });
  const [customFieldsValues, setCustomFieldsValues] = useState({});
  const [files, setFiles] = useState([]);

  // ✅ Resetear datos cuando cambia el documento
  useEffect(() => {
    if (document) {
      // ✅ Pre-llenar custom fields con valores del documento anterior
      const oldCustomData = document.custom_data || {};

      setFormData({
        issue_date: new Date().toISOString().split("T")[0],
        expiration_date: "",
        cost_amount: "",
        cost_payment_method: "transfer",
        notes: "",
      });
      setCustomFieldsValues(oldCustomData); // ✅ Copiar campos del doc anterior
      setFiles([]);
      setCurrentStep(1);
    }
  }, [document]);

  if (!isOpen || !document) return null;

  // ✅ Obtener configuración de campos personalizados
  const customFieldsConfig = document.document_type?.custom_fields || [];
  const requiresCost = document.document_type?.requires_cost || false;

  // ✅ Determinar los pasos según configuración
  const steps = [
    { id: 1, title: "Vigencia", icon: Calendar },
    ...(customFieldsConfig.length > 0
      ? [{ id: 2, title: "Datos del Documento", icon: Clipboard }]
      : []),
    {
      id: customFieldsConfig.length > 0 ? 3 : 2,
      title: "Archivos",
      icon: Upload,
    },
    ...(requiresCost
      ? [
        {
          id: customFieldsConfig.length > 0 ? 4 : 3,
          title: "Costos",
          icon: DollarSign,
        },
      ]
      : []),
  ];

  const totalSteps = steps.length;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    console.log("📤 Enviando renovación:", {
      formData,
      customFieldsValues,
      files_count: files.length,
    });

    // ✅ Incluir campos personalizados en el envío
    const submitData = {
      ...formData,
      custom_fields: customFieldsValues,
      attachments: files,
    };

    onSubmit(submitData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl border animate-slideUp max-h-[90vh] flex flex-col">
          {/* Header con Badge de Estado */}
          <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold text-gray-900">
                  Renovar Documento
                </h2>
                {/* ✅ Badge de estado */}
                {document &&
                  (() => {
                    const getDaysUntil = () => {
                      if (!document.expiration_date) return null;
                      const today = new Date();
                      const exp = new Date(document.expiration_date);
                      return Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
                    };
                    const days = getDaysUntil();

                    if (days === null) return null;
                    if (days < 0) {
                      return (
                        <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-full">
                          Vencido hace {Math.abs(days)} días
                        </span>
                      );
                    }
                    if (days <= 7) {
                      return (
                        <span className="px-2 py-1 text-xs font-bold bg-orange-100 text-orange-800 rounded-full">
                          Vence en {days} días
                        </span>
                      );
                    }
                    if (days <= 30) {
                      return (
                        <span className="px-2 py-1 text-xs font-bold bg-yellow-100 text-yellow-800 rounded-full">
                          Vence en {days} días
                        </span>
                      );
                    }
                    return (
                      <span className="px-2 py-1 text-xs font-bold bg-blue-100 text-blue-800 rounded-full">
                        Vigente {days} días más
                      </span>
                    );
                  })()}
              </div>
              {document && (
                <p className="text-sm text-gray-600 mt-1">
                  {document.documentable?.placa ||
                    `${document.documentable?.nombre || ""} ${document.documentable?.apellidos || ""}`.trim() ||
                    document.documentable?.nombre_comercial ||
                    "Sin información"}{" "}
                  - {document.document_type?.name}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Steps Indicator */}
          <div className="px-4 py-3 border-b bg-gray-50 flex-shrink-0">
            <div className="flex items-center justify-between overflow-x-auto">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.id} className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${currentStep >= step.id
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-500"
                        }`}
                    >
                      {currentStep > step.id ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>
                    <span
                      className={`ml-2 text-xs font-medium hidden sm:inline ${currentStep >= step.id
                        ? "text-gray-900"
                        : "text-gray-500"
                        }`}
                    >
                      {step.title}
                    </span>
                    {index < steps.length - 1 && (
                      <div
                        className={`w-8 h-0.5 mx-2 ${currentStep > step.id ? "bg-blue-600" : "bg-gray-200"
                          }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step Content */}
          <form
            onSubmit={handleSubmit}
            className="p-6 overflow-y-auto flex-1"
            style={{ minHeight: "320px" }}
          >
            {/* Step 1: Vigencia (Solo Fechas Universales) */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="doc_type" className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Documento
                  </label>
                  <input
                    id="doc_type"
                    type="text"
                    value={document?.document_type?.name || ""}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="issue_date" className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Emisión *
                    </label>
                    <input
                      id="issue_date"
                      type="date"
                      name="issue_date"
                      value={formData.issue_date}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="expiration_date" className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Vencimiento *
                    </label>
                    <input
                      id="expiration_date"
                      type="date"
                      name="expiration_date"
                      value={formData.expiration_date}
                      onChange={handleInputChange}
                      required
                      min={formData.issue_date}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* ✅ Nota informativa */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-blue-800">
                    ℹ️ Los datos específicos del documento (como número, entidad
                    emisora, etc.) se completarán en el siguiente paso según el
                    tipo de documento.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Campos Personalizados (solo si existen) */}
            {customFieldsConfig.length > 0 && currentStep === 2 && (
              <div>
                <div className="mb-4">
                  <h3 className="text-md font-bold text-gray-900 mb-2">
                    📋 Datos Específicos del Documento
                  </h3>
                  <p className="text-sm text-gray-600">
                    Completa los campos requeridos para este tipo de documento.
                  </p>
                </div>
                <CustomFields
                  fields={customFieldsConfig}
                  values={customFieldsValues}
                  onChange={setCustomFieldsValues}
                  disabled={false}
                />
              </div>
            )}

            {/* Step: Archivos */}
            {currentStep === (customFieldsConfig.length > 0 ? 3 : 2) && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="file_upload" className="block text-sm font-medium text-gray-700 mb-2">
                    Subir Archivos del Nuevo Documento
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-2">
                      Arrastra archivos o haz clic para seleccionar
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      PDF, JPG, PNG hasta 10MB
                    </p>
                    <label htmlFor="file_upload" className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 cursor-pointer inline-block">
                      <input
                        id="file_upload"
                        type="file"
                        multiple
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                      Seleccionar archivos
                    </label>
                  </div>
                </div>

                {files.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Archivos seleccionados ({files.length})
                    </p>
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-blue-900">
                              {file.name}
                            </p>
                            <p className="text-xs text-blue-600">
                              {(file.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step: Costos (solo si requires_cost) */}
            {requiresCost &&
              currentStep === (customFieldsConfig.length > 0 ? 4 : 3) && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Costo (S/.) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="cost_amount"
                      value={formData.cost_amount}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Método de Pago
                    </label>
                    <select
                      name="cost_payment_method"
                      value={formData.cost_payment_method}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="cash">💵 Efectivo</option>
                      <option value="transfer">🏦 Transferencia</option>
                      <option value="card">💳 Tarjeta</option>
                      <option value="check">📝 Cheque</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notas Adicionales
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Observaciones sobre el pago..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Nota:</strong> Al confirmar, el documento
                      anterior se marcará como no vigente y se generarán nuevas
                      alertas automáticamente.
                    </p>
                  </div>
                </div>
              )}
          </form>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50 flex-shrink-0">
            <button
              type="button"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Anterior
            </button>

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center transition-colors"
              >
                Siguiente
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center transition-colors"
              >
                <Check className="h-4 w-4 mr-1" />
                Confirmar Renovación
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
